type PushHandler = (data: unknown) => void

interface PendingCall {
  resolve: (result: unknown) => void
  reject: (error: Error) => void
  timer: ReturnType<typeof setTimeout>
}

/** Default timeout for two-way RPC calls (milliseconds) */
export const DEFAULT_CALL_TIMEOUT_MS = 60_000

/**
 * RPC Engine — communicates with the parent window (web platform) via postMessage.
 *
 * Security model:
 *   1. On init, the SDK sends { type: '__charx_rpc__', method: 'sdk.init' } to the parent.
 *   2. The platform responds with { type: '__charx_ready__', token, origin } — a per-session
 *      secret token that proves the parent is the legitimate platform.
 *   3. All subsequent outgoing RPC messages include the token.
 *   4. The platform validates the token on every call, rejecting unauthorised frames.
 *   5. Incoming messages are accepted only from window.parent (e.source check).
 *
 * Protocol (matches html-message.tsx):
 *   iframe → parent:  { type: '__charx_rpc__',          iframeId, token?, id?, method, args[] }
 *   parent → iframe:  { type: '__charx_ready__',         iframeId, token, origin }
 *   parent → iframe:  { type: '__charx_rpc_response__',  iframeId, id, result?, error? }
 *   parent → iframe:  { type: '__charx_push__',          event, data }
 */
export class RpcEngine {
  private pending = new Map<string, PendingCall>()
  private pushHandlers = new Map<string, Set<PushHandler>>()
  private readonly iframeId: string
  private readonly target: Window

  /** Session token issued by the platform after the init handshake */
  private _token: string | null = null
  /** Platform's origin — used as targetOrigin for all outgoing messages after handshake */
  private _platformOrigin: string | null = null
  /** Resolvers waiting for the __charx_ready__ handshake */
  private _readyResolvers: Array<() => void> = []
  private _handshakeDone = false

  constructor(target: Window = window.parent) {
    this.target = target
    this.iframeId = RpcEngine._genId()
    window.addEventListener('message', this.handleMessage)
  }

  private static _genId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `charx-sdk-${crypto.randomUUID()}`
    }
    // Fallback for environments without crypto.randomUUID
    return `charx-sdk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
  }

  private handleMessage = (e: MessageEvent) => {
    if (!e.data) return

    const messageIframeId = (e.data as { iframeId?: unknown }).iframeId
    if (messageIframeId !== undefined && messageIframeId !== this.iframeId) return
    const messageType = (e.data as { type?: unknown }).type

    // Some WebView hosts re-dispatch platform messages through a proxy source.
    // iframeId remains the trust boundary for directed handshake and RPC replies.
    if (
      e.source !== this.target
      && !(
        typeof messageIframeId === 'string'
        && messageIframeId === this.iframeId
        && (messageType === '__charx_ready__' || messageType === '__charx_rpc_response__')
      )
    ) return

    // Handshake: platform confirms the iframe is authorised and provides a session token
    if (messageType === '__charx_ready__') {
      const { token, origin } = e.data as { token: string; origin: string }
      if (typeof token === 'string' && token.length > 0) {
        this._token = token
        this._platformOrigin = origin || null
        this._handshakeDone = true
        this._readyResolvers.forEach(r => r())
        this._readyResolvers = []
      }
      return
    }

    // RPC response — platform may include an `error` field instead of `result`
    if (messageType === '__charx_rpc_response__') {
      const { id, result, error } = e.data as { id: string; result: unknown; error?: string }
      const pending = this.pending.get(id)
      if (pending) {
        clearTimeout(pending.timer)
        this.pending.delete(id)
        if (error) {
          pending.reject(new Error(error))
        } else {
          pending.resolve(result)
        }
      }
      return
    }

    // Push event from platform (e.g. variable.changed, chat.stream.chunk)
    if (messageType === '__charx_push__') {
      const { event, data } = e.data as { event: string; data: unknown }
      this.pushHandlers.get(event)?.forEach(h => h(data))
    }
  }

  /**
   * Returns a Promise that resolves when the platform handshake completes.
   * Used by CharxSdk.init() to wait for the session token.
   */
  waitReady(): Promise<void> {
    if (this._handshakeDone) return Promise.resolve()
    return new Promise(resolve => this._readyResolvers.push(resolve))
  }

  get iframeIdValue(): string { return this.iframeId }

  /**
   * Two-way call — returns a Promise that resolves with the platform's response.
   * Rejects if the platform returns an error or if `timeoutMs` elapses with no response.
   */
  call<Resp = unknown>(method: string, args: unknown[] = [], timeoutMs = DEFAULT_CALL_TIMEOUT_MS): Promise<Resp> {
    return new Promise((resolve, reject) => {
      const id = `${method}_${Date.now()}_${Math.random().toString(36).slice(2)}`
      const timer = setTimeout(() => {
        if (this.pending.has(id)) {
          this.pending.delete(id)
          reject(new Error(`[CharxSdk] RPC timeout: ${method}`))
        }
      }, timeoutMs)
      this.pending.set(id, { resolve: resolve as (d: unknown) => void, reject, timer })
      this.target.postMessage(
        { type: '__charx_rpc__', iframeId: this.iframeId, token: this._token, id, method, args },
        this._platformOrigin ?? '*',
      )
    })
  }

  /** Fire-and-forget — no response expected. */
  post(method: string, args: unknown[] = []): Promise<void> {
    this.target.postMessage(
      { type: '__charx_rpc__', iframeId: this.iframeId, token: this._token, method, args },
      this._platformOrigin ?? '*',
    )
    return Promise.resolve()
  }

  /** Subscribe to push events sent by the platform */
  push(event: string, handler: PushHandler): () => void {
    if (!this.pushHandlers.has(event)) {
      this.pushHandlers.set(event, new Set())
    }
    this.pushHandlers.get(event)!.add(handler)
    return () => this.pushHandlers.get(event)?.delete(handler)
  }

  /** Destroy the engine — rejects all pending calls and removes the message listener */
  destroy(): void {
    window.removeEventListener('message', this.handleMessage)
    this.pending.forEach(({ reject, timer }) => {
      clearTimeout(timer)
      reject(new Error('[CharxSdk] SDK destroyed'))
    })
    this.pending.clear()
    this.pushHandlers.clear()
  }
}
